import { Box, Grid } from '@mui/material';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { closeJob, IJob } from 'wisestep-api';
import {
  Button,
  ControlledTextInput,
  Item,
  WsColors,
  WsDialog,
  WsTypography,
  WsTypoVariants,
  Spinner,
} from 'wisestep-base';

import { failed, success } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import useCloseJobReasonsSettings from '../../../hooks/useCloseJobReasonsSettings';

export interface jobClosingDialogueProps {
  open: boolean;
  job: IJob;
  onClose: () => void;
  pageRefresh?: () => void;
}

const CloseJobDialogue = (props: jobClosingDialogueProps) => {
  const { open, onClose, job, pageRefresh } = props;
  const [closingReason, setClosingReason] = useState<string>('');
  const [otherReasonSelected, setOtherReasonSelected] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm();
  const { handleSubmit } = methods;
  const jobClosingReasons = useCloseJobReasonsSettings().reasons;
  const isLoading = useCloseJobReasonsSettings().id === '';

  const onReasonSelect = (value: string) => {
    if (!value) {
      return;
    }
    if (value === 'Other reason') {
      setClosingReason('');
      setOtherReasonSelected(true);
    } else {
      setClosingReason(value);
      setOtherReasonSelected(false);
    }
  };

  const onSave = async () => {
    job.jobCloseReason = [closingReason];
    job.isClosedForAssigningRecruiters = true;
    try {
      await closeJob(job);
      pageRefresh && pageRefresh();
      setNotification(success("Job and all it's Work Assignments closed successfully."));
      onClose();
    } catch (e) {
      setNotification(failed('Failed to close the job. Please try again.'));
    }
  };

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <WsDialog openDialog={open}>
          <Box style={{ margin: '4%' }}>
            <Box display="flex" justifyContent="center">
              <WsTypography variant={WsTypoVariants.TITLE}>Reason for Closing a Job</WsTypography>
            </Box>
            <FormProvider {...methods}>
              <Box textAlign="left" component="form" onSubmit={handleSubmit(onSave)} ml={5} mt={3}>
                <Grid container rowSpacing={4}>
                  {jobClosingReasons?.map((record, index) => (
                    <>
                      <Grid item xs={8} key={index}>
                        {record}
                      </Grid>
                      <Grid item xs={2} textAlign="right">
                        <Button
                          type="button"
                          onClick={() => onReasonSelect(record)}
                          variant="outlined"
                          sx={{
                            backgroundColor:
                              closingReason === record ? WsColors.TheBlues100 : WsColors.White,
                          }}
                        >
                          Select
                        </Button>
                      </Grid>
                    </>
                  ))}

                  {otherReasonSelected && (
                    <Grid item xs={10}>
                      <Item>
                        <ControlledTextInput
                          name="closingReason"
                          labelText=""
                          type="text"
                          fullWidth
                          id="outlined-basic"
                          placeholder="Type new reason for closing a job"
                          variant="outlined"
                          required={otherReasonSelected}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setClosingReason(event.target.value)
                          }
                          onBlur={(
                            event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
                          ) => onReasonSelect((event.target as HTMLInputElement).value)}
                        />
                      </Item>
                    </Grid>
                  )}
                  <Grid item xs={5} textAlign="right">
                    <Button
                      type="button"
                      disabled={closingReason === ''}
                      onClick={() => onSave()}
                      variant="contained"
                    >
                      Save
                    </Button>
                  </Grid>
                  <Grid item xs={2} textAlign="right">
                    <Button type="button" onClick={() => onClose()} variant="contained">
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </FormProvider>
          </Box>
        </WsDialog>
      )}
    </>
  );
};

export default CloseJobDialogue;
